/**
 * N6 — der Reiter „Refund-Entscheidungen“ der Seite „Kundenrechnungen“
 * (`/invoicing?tab=refunds`).
 *
 * Ein Mensch schliesst eine `offen`e Refund-Entscheidung, die **kein** Forge-Dokument
 * braucht oder anderswo erledigt wurde — mit einem Grund aus der GESCHLOSSENEN Liste
 * und dem Konto als Urheber (P5-O4, N-O5). Danach kann der Refund in Forge nicht mehr
 * korrigiert werden (gewollt, gegen doppelte Minderung).
 *
 * Zwei Routen, beide hinter dem Doppel-Riegel (JWT-Tor + `@require_auth`):
 *
 *   GET  /api/invoicing/admin/refund-entscheidungen?zustand=offen|ausstehend
 *   POST /api/invoicing/admin/refund-entscheidungen/<refund_id>/schliessen
 *
 * 🔴 **Kein Freitext.** Die Codeliste ist die geschlossene Menge des Backends
 *    (`refund_entscheidung.SCHLIESSGRUENDE`); sie steht hier als Beschriftungsmap und
 *    ist in `__tests__/RefundEntscheidungen.test.jsx` gegen die Quelle gepinnt. Ein
 *    unbekannter Code wäre 422 — die Seite bietet ihn gar nicht erst an.
 *
 * 🔴 **Die BB-Beleg-ID ist nur bei `extern_korrigiert` Pflicht** — die Minderung liegt
 *    dann ausserhalb Forge (BB von Hand). Das Feld erscheint nur bei diesem Grund.
 *
 * 🔴 **Zustände wie S5.4-F:** erstes Laden = Skeletons (kein Spinner), Neuladen lässt
 *    die Daten stehen (`LinearProgress`, `aria-busy`), ein Fehler zeigt KEINE alte
 *    Zahl. Die leere Liste nennt ihren Grund.
 */
import { useCallback, useEffect, useId, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import RefreshButton from '../../components/common/RefreshButton'
import { api } from '../../api/client'
import {
  FS,
  PANEL_SX,
  SP,
  TEXT_LINK_TREFFERFLAECHE_SX,
  TEXT_LINK_TREFFERFLAECHE_OHNE_UNTERRAND_SX,
} from '../../theme/tokens'
import KorrekturDialog, { ZULAESSIGE_ARTEN } from './KorrekturDialog'
// german-words: ok: Importpfad, kein Sichttext
import AusfuhrKorrekturDialog from './AusfuhrKorrekturDialog'
import BestellLink from './BestellLink'
import DisabledReason from '../../components/common/DisabledReason';

// ── Beschriftungen ─────────────────────────────────────────────────────────
// Die ZEHN Arten der Weiche (`backend/invoicing/refund_entscheidung.ARTEN`) — die
// Entscheidungsart, NICHT `invoice.document_type` (`beschriftungen.ART_LABEL` deckt
// nur Rechnung/Korrektur und ist hier die falsche Menge). Unbekanntes bleibt roh.
export const ART_ENTSCHEIDUNG_LABEL = {
  kein_geld: 'No money',
  ohne_rechnung: 'Without invoice',
  vor_ausstellung: 'Before issuance',
  ausstehend: 'Pending',
  korrektur_positionen: 'Correction (items)',
  korrektur_betrag: 'Correction (amount)',
  steuerkorrektur: 'Tax correction',
  zahlungsstoerung: 'Payment disruption',
  korrektur_aus: 'Correction (out)',
  mensch: 'Human',
}

// Die geschlossene Menge der Schliessgründe (`backend/invoicing/refund_entscheidung.py`,
// `SCHLIESSGRUENDE`). Reihenfolge wie im Plan N6. Kein `sonstiges`.
export const GRUND_LABEL = {
  extern_korrigiert: 'Externally corrected (BB by hand)',
  zahlungsstoerung_geklaert: 'Payment disruption resolved',
  selbstausfuhr_extern: 'Self-export external',
  kein_entgelt: 'No consideration',
  doppelt: 'Duplicate',
}
export const GRUND_REIHENFOLGE = [
  'extern_korrigiert',
  'zahlungsstoerung_geklaert',
  'selbstausfuhr_extern',
  'kein_entgelt',
  'doppelt',
]
//: Bei diesem Grund ist die BB-Beleg-ID Pflicht (Minderung ausserhalb Forge).
export const GRUND_MIT_BB_BELEG = 'extern_korrigiert'

/**
 * S4a — der Grund, bei dem ein Lexware-Altbeleg ohne Forge-Rechnung leicht
 * verwechselt wird (Fehlgriff vom 01.11.: SHOP-14392 wurde so geschlossen, und eine
 * `erledigt`-Zeile hat keinen Rueckweg).
 */
export const GRUND_SELBSTAUSFUHR = 'selbstausfuhr_extern'

const STRICH = '—'

// ── Ausfuhr-Korrektur bei Erstattung (Plan 2026-10-28, Task 5) ─────────────
/**
 * Die Arten, an denen „Issue export correction" steht — deckungsgleich mit
 * `ausfuhr_korrektur_routes.EINSTIEGSARTEN` (Tor 9a `steuerkorrektur`, Tor 3 `mensch`);
 * gepinnt in `__tests__/RefundEntscheidungen.test.jsx`.
 */
export const AUSFUHR_EINSTIEGSARTEN = ['steuerkorrektur', 'mensch']

/**
 * Die Sätze an einer Ausfuhrzeile (`ausfuhr_art != null`). Eine Ausfuhrzeile hat NIE
 * „Create correction": der Handweg antwortet dort sicher 409 `ausfuhrvorgang`.
 *
 *   aus               Modus `off` — der Knopf fehlt, der Grund steht da
 *   erwartet …        je Nachweis-Zustand ein Satz mit NÄCHSTEM SCHRITT (R16 W2): die vier
 *   abgelehnt         Zustände aus `export_proof.ARTEN` ohne `geprueft`. `abgelehnt` und
 *                     `abgelaufen` versprechen kein Warten („yet") — dort kommt nichts mehr
 *                     von selbst; ein neuer Nachweis geht nur über den Upload in Forge (BA7).
 *                     Neben dem Satz steht der Link „Open invoice" (dort liegen Upload und
 *                     Freigabe, BA3b/BA7).
 *   handfall          eine Art, die weder in die Handkorrektur noch zum Knopf führt
 *   weg               unter dem Weichengrund, wo der Knopf steht (Grill W14, R16 W3): die
 *                     Weiche schreibt für `geprueft` weiter „… Handfall (K4-CH)", und dieser
 *                     Plan lässt sie unberührt — der Satz sagt, dass er die Zeile darüber
 *                     ablöst. ⚠️ Im Modus `pilot` steht er auch an Bestellungen ausserhalb
 *                     der Pilotliste: die Liste kennt nur `modus`, nicht die Liste
 *                     (`refund_entscheidung_routes._ausfuhr_kopf`); der Dialog nennt dort
 *                     den Sperrgrund beim Öffnen.
 */
export const AUSFUHR_HINWEIS = {
  aus: 'Export corrections are switched off — handle this refund manually.',
  erwartet: 'No export proof yet — upload it on the invoice and approve it.',
  eingegangen: 'Export proof not approved yet — approve it on the invoice.',
  abgelaufen:
    'No export proof arrived in time — upload one on the invoice and approve it, or handle this refund manually.',
  abgelehnt:
    'Export proof rejected — upload a new one on the invoice and approve it, or handle this refund manually.',
  handfall: 'Self-export case — handle this refund manually.',
  weg: 'The routing reason above predates this button — use ‘Issue export correction’.',
}

/**
 * Was eine Zeile zur Ausfuhr-Korrektur zeigt: `{ knopf, satz, zurRechnung }`.
 * `zurRechnung` = neben dem Satz steht der Link auf die Rechnung (Nachweis-Sätze).
 * `modus` kommt aus dem Listenkopf (`ausfuhr_korrektur.modus`); fehlt er, gilt `off`
 * (fail-closed — ein Server vor Task 4 kennt die Routen nicht).
 */
export function ausfuhrLage(e, modus) {
  if (e?.ausfuhr_art == null) return { knopf: false, satz: null, zurRechnung: false }
  const moeglich = e.invoice_id != null && AUSFUHR_EINSTIEGSARTEN.includes(e.art)
  if (!moeglich) {
    // Hier stand bis Task 5 „Create correction" — die Zeile sagt jetzt, warum nicht.
    const warHandweg = e.invoice_id != null && ZULAESSIGE_ARTEN.includes(e.art)
    return { knopf: false, satz: warHandweg ? AUSFUHR_HINWEIS.handfall : null, zurRechnung: false }
  }
  if (modus === 'off') return { knopf: false, satz: AUSFUHR_HINWEIS.aus, zurRechnung: false }
  if (e.ausfuhr_art !== 'geprueft') {
    return {
      knopf: false,
      satz: AUSFUHR_HINWEIS[e.ausfuhr_art] || AUSFUHR_HINWEIS.eingegangen,
      zurRechnung: true,
    }
  }
  return { knopf: true, satz: null, zurRechnung: false }
}

const fehlertext = (e) => e?.response?.data?.error || e?.message || 'Unknown error'

/**
 * Die Fehlercodes des Schliess-POST (`refund_entscheidung_routes.py`) als Satz mit
 * Ursache und nächstem Schritt — **kein roher Code** in der Oberfläche (R16-W1).
 * 403 `kein_schreibkonto`, 422 `unbekannter_schliessgrund`/`bb_beleg_id_fehlt`/
 * `bb_beleg_id_ungueltig`/`ausfuhr_nachweis_nicht_geprueft` (BA5). Sonst ein Satz ohne Code.
 */
export const SCHLIESS_FEHLER = {
  kein_schreibkonto:
    'This account may not close refund decisions. Sign in with the write account and try again.',
  unbekannter_schliessgrund:
    'The selected reason is unknown. Reload the list and pick a reason from the options.',
  bb_beleg_id_fehlt:
    'For “Externally corrected” the BB receipt ID is missing. Enter the receipt number and close again.',
  bb_beleg_id_ungueltig:
    'The BB receipt ID may contain digits only. Correct the number and close again.',
  ausfuhr_nachweis_nicht_geprueft:
    'This invoice has no approved export proof. Upload a new proof if it is missing or was rejected, approve it, then close again.',
}

const SCHLIESS_FEHLER_RUECKFALL = 'Closing failed. Reload the list and try again.'

const schliessFehlertext = (e) => SCHLIESS_FEHLER[e?.response?.data?.error] || SCHLIESS_FEHLER_RUECKFALL

/**
 * Ein Lesepfad mit Laden/Fehler/Neuladen (Muster `Betrieb.jsx`).
 * `params === null` heisst: die Eingabe ist ungültig — es geht KEINE Anfrage hinaus.
 * Ein Fehler löscht die Daten: eine Zahl, deren Abruf scheiterte, darf nicht stehen bleiben.
 */
function useLesen(pfad, params) {
  const schluessel = params === null ? null : JSON.stringify(params)
  const [stand, setStand] = useState({ daten: null, laedt: schluessel !== null, fehler: null })
  const [runde, setRunde] = useState(0)

  useEffect(() => {
    if (schluessel === null) {
      setStand((alt) => (alt.laedt ? { ...alt, laedt: false } : alt))
      return undefined
    }
    let abgemeldet = false
    setStand((alt) => ({ ...alt, laedt: true, fehler: null }))
    api.get(pfad, { params: JSON.parse(schluessel) })
      .then(({ data }) => { if (!abgemeldet) setStand({ daten: data, laedt: false, fehler: null }) })
      .catch((e) => { if (!abgemeldet) setStand({ daten: null, laedt: false, fehler: fehlertext(e) }) })
    return () => { abgemeldet = true }
  }, [pfad, schluessel, runde])

  const neuLaden = useCallback(() => setRunde((n) => n + 1), [])
  return { ...stand, neuLaden }
}

/**
 * Die Schliessmaske. `onErgebnis(text)` meldet den Ausgang an die Seite — sie lädt neu
 * und hält den Satz. Ein 409 heisst: die Zeile war nicht mehr `offen` (jemand war
 * schneller) — das ist kein Fehler des Bedieners, sondern ein Neuladen.
 */
function SchliessMaske({ offen, zeile, onSchliessen, onErgebnis }) {
  const titelId = useId()
  const [grund, setGrund] = useState('')
  const [bbBelegId, setBbBelegId] = useState('')
  const [sendet, setSendet] = useState(false)
  const [fehler, setFehler] = useState(null)

  useEffect(() => {
    if (!offen) return
    setGrund('')
    setBbBelegId('')
    setFehler(null)
  }, [offen])

  const brauchtBeleg = grund === GRUND_MIT_BB_BELEG
  const belegFehlt = brauchtBeleg && !/^[0-9]+$/.test(bbBelegId.trim())
  const unvollstaendig = !grund || belegFehlt

  const sende = (e) => {
    e.preventDefault()
    if (unvollstaendig || sendet) return
    setSendet(true)
    setFehler(null)
    const koerper = { grund }
    if (brauchtBeleg) koerper.bb_beleg_id = bbBelegId.trim()
    api.post(`/api/invoicing/admin/refund-entscheidungen/${zeile.refund_id}/schliessen`, koerper)
      .then(() => {
        setSendet(false)
        onErgebnis(`Decision for refund ${zeile.refund_id} closed.`)
      })
      .catch((err) => {
        setSendet(false)
        if (err?.response?.status === 409) {
          onErgebnis(`Refund ${zeile.refund_id} was no longer open — the list has been reloaded.`)
          return
        }
        setFehler(schliessFehlertext(err))
      })
  }

  return (
    <Dialog
      open={offen}
      onClose={sendet ? undefined : onSchliessen}
      aria-labelledby={titelId}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { component: 'form', onSubmit: sende, noValidate: true } }}
    >
      <DialogTitle id={titelId}>Close refund decision</DialogTitle>
      <DialogContent>
        <Stack spacing={SP.card} sx={{ pt: SP.chip }}>
          {/* S4a: der Bestellname als eigene Zeile ueber dem Satz — der Fehlgriff
              vom 01.11. schloss SHOP-14392, weil der Dialog nur eine Nummer zeigte.
              🔴 Die Zeile traegt ihr Nomen („Order"): im Rueckfall steht hier sonst
              eine nackte Zahl, und genau die Population ohne `invoice_id` sind die
              Lexware-Altzeilen des Fehlgriffs (R16 C1). `subtitle1` statt `h6`, weil
              der Dialogtitel schon `h6` ist — zwei gleich laute Ueberschriften
              fallen nicht ab (R16 W3). Der Satz darunter nennt die Bestellung NICHT
              noch einmal: zwei Kennungen nackt untereinander sind nicht lesbarer,
              sondern nur doppelt (R16 W2). */}
          <Typography variant="subtitle1" component="p" data-testid="refunds-bestellname"
            sx={{ wordBreak: 'break-word' }}>
            Order <BestellLink eintrag={zeile} />
          </Typography>
          <Typography variant="body2">
            Refund {zeile?.refund_id} will be closed without a document. After that
            the refund can no longer be corrected in Forge.
          </Typography>
          {/* 🔴 E3 (Handweg ohne Forge-Original, 2026-10-12): die Zeile hätte ohne
              E2 in die Handkorrektur geführt, hat aber kein gespeichertes Original
              — der Grund steht hier, statt erst als 409 nach dem Klick. Der Satz
              nennt kein System UND keinen Schliessgrund: wo die Minderung eines
              Lexware-Altbelegs gebucht wird und ob `extern_korrigiert` dafür passt,
              ist eine offene Fachfrage (Karte
              `docs/backlog-auto-repair/2026-10-12-handweg-kennt-keinen-lexware-altbeleg.md`
              §Offen). Ein Beispielgrund hier nähme sie vorweg (R16 W4(c),
              Wiedervorlage 1). */}
          {zeile?.invoice_id == null && ZULAESSIGE_ARTEN.includes(zeile?.art) && (
            <Alert severity="info">
              No Forge invoice is stored for this refund decision, so a correction cannot be
              created here. Once the refund has been handled outside Forge, close the decision
              here with the reason that matches how it was handled.
            </Alert>
          )}
          <FormControl component="fieldset" disabled={sendet}>
            <FormLabel component="legend">Reason</FormLabel>
            <RadioGroup
              value={grund}
              onChange={(e) => { setGrund(e.target.value); setFehler(null) }}
            >
              {GRUND_REIHENFOLGE.map((wert) => (
                <FormControlLabel
                  key={wert}
                  value={wert}
                  control={<Radio size="small" />}
                  label={GRUND_LABEL[wert]}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {/* S4a — direkt unter der Grundauswahl, nur bei `selbstausfuhr_extern` UND
              ohne Forge-Rechnung. Wortlaut aus dem Plan; er sperrt nichts, „Close"
              bleibt aktiv (O-2: der Grund bleibt bei Lexware-Bestellungen frei). */}
          {zeile?.invoice_id == null && grund === GRUND_SELBSTAUSFUHR && (
            <Alert severity="warning">
              No Forge invoice is linked to this refund. Only for a Lexware order the
              customer exported themselves.
            </Alert>
          )}
          {brauchtBeleg && (
            <TextField
              label="BB receipt ID"
              size="small"
              fullWidth
              autoComplete="off"
              value={bbBelegId}
              onChange={(e) => { setBbBelegId(e.target.value); setFehler(null) }}
              error={Boolean(bbBelegId) && belegFehlt}
              helperText="Required for “Externally corrected” — the BB receipt number (digits only)."
              disabled={sendet}
            />
          )}
          {fehler && <Alert severity="error">{fehler}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSchliessen} disabled={sendet}>Cancel</Button>
        <DisabledReason
          reason={sendet
            ? null
            : !grund ? 'Select a reason first.'
              : belegFehlt ? 'Enter the bookkeeping receipt number first.' : null}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={unvollstaendig || sendet}
            startIcon={sendet ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : null}
          >
            Close
          </Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Der Reiter „Refund-Entscheidungen“.
 *
 * 🔴 **S2 (2026-11-01): der Testbelege-Schalter steht in der Leiste dieses
 *    Panels.** Er stand bis S2 in der KPI-Zone der Schale; die KPI-Reihe ist
 *    seither im Panel von „Invoices“. Der Schalter bleibt EIN Zustand
 *    (`testbelege=1`) und hat EINE Schreibstelle — `setzeTestbelege` kommt als
 *    Prop von der Schale (F2.7). S4 gestaltet diese Leiste um; die Platzierung
 *    ist S2.
 */
export default function RefundEntscheidungen({ setzeTestbelege }) {
  const theme = useTheme()
  const schmal = useMediaQuery(theme.breakpoints.down('sm'))
  const [zeile, setZeile] = useState(null)
  const [korrektur, setKorrektur] = useState(null)
  const [ausfuhr, setAusfuhr] = useState(null)
  const [meldung, setMeldung] = useState(null)   // { text, ton }
  // 🔴 Derselbe Seitenschalter (F2.7): Testbelege standardmäßig aus der Menschenliste.
  const [searchParams] = useSearchParams()
  const testbelege = searchParams.get('testbelege') === '1'
  const liste = useLesen('/api/invoicing/admin/refund-entscheidungen', {
    zustand: 'offen', ...(testbelege ? { testbelege: 1 } : {}),
  })
  const { neuLaden } = liste

  const schliessen = useCallback((text) => {
    setZeile(null)
    setMeldung({ text, ton: 'success' })
    neuLaden()
  }, [neuLaden])

  // S5.6a / R16-B2: der Ausgang der Handkorrektur färbt die Meldung. Ein
  // `gesperrt`/`uebersprungen` ist KEIN Erfolg — fest `success` meldete dem
  // Bediener grün „Die Korrektur wurde nicht ausgestellt." Dieselbe Regel wie
  // `InvoiceDetail.meldeKorrektur` (`ausgestellt` → success, sonst warning).
  //
  // 🔴 S5.6a: Der Dialog wird hier NICHT mehr geschlossen. `KorrekturDialog`
  // zeigt seinen Ausgang im Ergebnisblock (Satz, Nummer, Link „Zum Dokument",
  // die zeilenlosen Gründe aus R16-B1) — `setKorrektur(null)` hing ihn im
  // SELBEN Zug aus, in dem `stelleAus` diesen Block setzte, und machte ihn damit
  // unerreichbar. `gesperrt` ist heute der Hauptpfad (Textriegel leer, P4-O1):
  // der Bediener sah die Gründe nie. Geschlossen wird jetzt vom Bediener —
  // „Abbrechen"/Esc oder der Link „Zum Dokument", der `onSchliessen` selbst
  // ruft. Dieselbe Form wie `InvoiceDetail.meldeKorrektur`.
  const korrekturFertig = useCallback((text, ausgang) => {
    setMeldung({ text, ton: ausgang?.zustand === 'ausgestellt' ? 'success' : 'warning' })
    neuLaden()
  }, [neuLaden])

  // Der Ausgang der Ausfuhr-Korrektur färbt die Meldung (Grill W14): nur `ausgestellt`
  // ist ein Erfolg; `schon_erledigt` mit Nummer ist eine Information, ohne Dokument und
  // der unbekannte Ausgang sind Warnungen. Der Dialog bleibt offen (Muster oben).
  const ausfuhrFertig = useCallback((text, ausgang) => {
    const ton = ausgang?.zustand === 'ausgestellt' ? 'success'
      : ausgang?.zustand === 'schon_erledigt' && ausgang?.nummer && !ausgang?.ohne_dokument
        ? 'info' : 'warning'
    setMeldung({ text, ton })
    neuLaden()
  }, [neuLaden])

  const daten = liste.daten
  const eintraege = daten?.items || []
  const ausfuhrModus = daten?.ausfuhr_korrektur?.modus || 'off'
  // 🔴 Erstaufbau = derselbe Ausdruck wie das Skelett unten
  // (`data-testid="refunds-laden"`). Der Refresh-Knopf dreht sich dann nicht
  // mit: der Aufbau gehoert den Skeletten, der Spinner gehoert dem NEULADEN
  // vorhandener Daten (Anti-Pattern 11).
  const ersterLadevorgang = !liste.fehler && daten === null && liste.laedt

  return (
    <>
        {/* 🔴 E8 (2026-10-07): der Refresh stand als `actions`-Eintrag im Kopf.
            Ein Refresh ist nie primaer — und im Kopf rendert die EINZIGE
            Aktion ohne `variant` `contained` (E2). */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: SP.chip }}>
          <RefreshButton
            ariaLabel="Refresh refund decisions"
            loading={liste.laedt}
            firstLoad={ersterLadevorgang}
            onClick={neuLaden}
          />
        </Box>

        {/* 🔴 S2 (L3/F2.7): die Filterzeile dieses Panels — S4 gestaltet sie zur
            vollen Z1 um. Hier steht heute genau der Testbelege-Schalter: EIN
            Zustand, EINE Schreibstelle (die Schale reicht `setzeTestbelege`
            herein), hier nur das Bedienelement. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mb: SP.chip }}
          data-testid="refunds-filterzeile"
        >
          <FormControlLabel
            control={(
              // 🔴 R16 Warnung 4 (2026-11-01): die Prop hat keinen Rückfall.
              //    Die Hausform für so eine Prop ist der WÄCHTER, nicht der
              //    Unfall — `Betrieb.jsx:3103` prüft `meldeKopf` vor dem Aufruf.
              //    Heute gibt es genau einen Aufrufer (`InvoiceList.jsx`), aber
              //    ein zweiter ohne Setter stürzte hier ab, statt still nichts zu
              //    tun. Ohne Setter bleibt der Schalter stehen (er ist
              //    kontrolliert über den URL-Zustand) — kein Absturz.
              <Switch
                size="small"
                checked={testbelege}
                onChange={(e) => { if (setzeTestbelege) setzeTestbelege(e.target.checked) }}
                inputProps={{ 'aria-label': 'Show test invoices' }}
              />
            )}
            label="Show test invoices"
          />
        </Stack>
        <Stack spacing={SP.section}>
          {meldung && (
            <Alert
              severity={meldung.ton}
              onClose={() => setMeldung(null)}
              data-testid="meldung"
            >
              {meldung.text}
            </Alert>
          )}

          {liste.fehler && (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={neuLaden}>Try again</Button>}
            >
              <AlertTitle>Refund decisions could not be loaded</AlertTitle>
              {liste.fehler}
            </Alert>
          )}

          {!liste.fehler && daten === null && liste.laedt && (
            <Paper elevation={0} sx={PANEL_SX} data-testid="refunds-laden" aria-busy="true">
              <Stack spacing={SP.tight}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} data-testid="refunds-skelett" variant="rounded" height={32} />
                ))}
              </Stack>
            </Paper>
          )}

          {!liste.fehler && daten !== null && (
            <Box aria-busy={liste.laedt ? 'true' : 'false'}>
              {liste.laedt && <LinearProgress aria-label="Updating" sx={{ mb: SP.chip }} />}
              {eintraege.length === 0 ? (
                <Alert severity="info">
                  No open refund decisions — the human list is empty.
                </Alert>
              ) : (
                // Breite Tabelle scrollt in ihrem Paper, nicht die Seite (Muster
                // `Betrieb.jsx`) — auf ≤600 px sonst Seitenüberlauf (R16-W3).
                <Paper elevation={0} sx={{ ...PANEL_SX, overflowX: 'auto' }}>
                  <Table
                    size="small"
                    aria-label="Open refund decisions"
                    // Teil A.1: am Telefon tragen vier Spalten 4 x 32 px Polster.
                    // Mit 8 px je Seite passt die Tabelle ins Papier (367 -> 303 px
                    // gegen 332 px Inhaltsbreite) — sonst bleibt sie 35 px breiter,
                    // das Papier scrollt und die Spalte „Action" wird beschnitten.
                    // Ab `sm` bleibt MUI's Polster (16 px) unveraendert.
                    sx={{ '& .MuiTableCell-root': { px: { xs: SP.tight } } }}
                  >
                    <TableHead>
                      <TableRow>
                        {/* 🔴 S4a-NB1: auf dem Telefon traegt die ERSTE Spalte die
                            Bestellung, nicht die Refund-ID. Die Refund-ID bleibt im
                            Schliessdialog lesbar; eine Spalte „Refund" neben „Order"
                            passt unter `sm` nicht und die Bestellung ist das, woran
                            ein Bediener die Zeile erkennt (Foto 390). */}
                        <TableCell>{schmal ? 'Order' : 'Refund'}</TableCell>
                        {!schmal && <TableCell>Order</TableCell>}
                        {!schmal && <TableCell>Number</TableCell>}
                        <TableCell>Type</TableCell>
                        {!schmal && <TableCell>Routing reason</TableCell>}
                        <TableCell>Age</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Kein `hover`: die Zeile selbst ist nicht klickbar —
                          gehandelt wird in den Zellen (Bestellverweis, Aktionen).
                          Kein `stopPropagation` noetig: `<TableRow>` traegt
                          keinen `onClick` (Muster `Betrieb.jsx`). */}
                      {eintraege.map((e) => {
                        const lage = ausfuhrLage(e, ausfuhrModus)
                        return (
                        <TableRow key={e.refund_id}>
                          <TableCell sx={{ fontSize: FS.sm }}>
                            {schmal ? (
                              <>
                                {/* R16 C1 (Runde 2): der Bestellname bleibt auch in
                                    der schmalen Zelle einzeilig — dieselbe
                                    Entscheidung wie die breite Zelle (`:475`). Das
                                    Token setzt `inline-flex`; das Textkind ist damit
                                    ein schrumpfbares Flex-Element, und mit dem
                                    Symbol daneben bricht „SHOP-14392" am
                                    Bindestrich. `nowrap` sitzt am VERWEIS, nicht an
                                    der Zelle: die Zelle traegt auch die
                                    Refund-ID-Zeile, und der Schliessdialog behaelt
                                    sein `wordBreak: 'break-word'`. */}
                                <BestellLink eintrag={e} einzeilig />
                                {/* R16 W1: unter `sm` traegt die Zelle die Bestellung,
                                    die Refund-ID bleibt als gedaempfte zweite Zeile
                                    daneben — sonst sind zwei offene Entscheidungen
                                    derselben Bestellung nicht unterscheidbar. */}
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: 'block',
                                    // 🔴 R16 C1: die 44-px-Trefferflaeche des Verweises
                                    // endet an seiner AUSSENkante; ohne diesen Abstand
                                    // beginnt die ID-Zeile in ihrem unteren Polster und
                                    // nimmt dem Finger die Flaeche. Der Abstand ist genau
                                    // dieses Polster — dieselbe Tokenzahl, kein Literal.
                                    // 🔴 R16 B1: der Token traegt `paddingBottom` als
                                    // String MIT `px`; als nackte Zahl rechnete `sx`
                                    // den Rand mit dem Spacing-Faktor 8 — aus 14
                                    // wuerden 112.
                                    mt: TEXT_LINK_TREFFERFLAECHE_SX.paddingBottom,
                                    fontFamily: 'monospace',
                                    fontSize: FS.xs,
                                  }}
                                >
                                  {e.refund_id}
                                </Typography>
                              </>
                            ) : e.refund_id}
                          </TableCell>
                          {!schmal && (
                            // R16 W4: `nowrap` — sonst bricht „SHOP-14392" bei 900
                            // mitten im Namen; das Paper scrollt bei Bedarf.
                            <TableCell sx={{ fontSize: FS.sm, whiteSpace: 'nowrap' }}>
                              <BestellLink eintrag={e} />
                            </TableCell>
                          )}
                          {!schmal && (
                            <TableCell sx={{ fontSize: FS.sm }}>{e.nummer || STRICH}</TableCell>
                          )}
                          <TableCell sx={{ fontSize: FS.sm }}>{ART_ENTSCHEIDUNG_LABEL[e.art] || e.art}</TableCell>
                          {!schmal && (
                            <TableCell sx={{ fontSize: FS.sm }}>
                              {e.grund || STRICH}
                              {lage.knopf && (
                                <Typography
                                  variant="caption"
                                  component="p"
                                  sx={{ color: 'text.secondary', mt: SP.tight }}
                                >
                                  {AUSFUHR_HINWEIS.weg}
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          {/* 🔴 R16 W1 (Runde 3), hingenommen statt „nachgebessert":
                              die AGE-Zelle traegt absichtlich KEIN `nowrap`. S4b
                              verbreitert die Order-Zelle um Symbol und Abstand
                              (~16 px); die Tabelle verteilt die feste Breite neu,
                              und ein zweistelliges Alter („12 d") bricht am Telefon
                              auf zwei Zeilen — A/B gemessen an `rechs4f` (Basis
                              einzeilig, Zweig zweizeilig). `nowrap` hier waere die
                              schlechtere Abhilfe: die min-content-Breite der Tabelle
                              waechst, das Papier scrollt, und „Close" rutscht aus
                              dem ersten Blick. Nicht „nachbessern". */}
                          <TableCell sx={{ fontSize: FS.sm }}>
                            {e.alter_tage == null ? STRICH : `${e.alter_tage} d`}
                          </TableCell>
                          <TableCell align="right">
                            {/* S5.6a: nur die drei zugelassenen Arten führen in die
                                Handkorrektur — der Rest wird geschlossen. */}
                            {/* 🔴 E2 (Handweg ohne Forge-Original, 2026-10-12): UND
                                nur, wenn ein Forge-Original gespeichert ist. Die
                                Bedingung ist **genau** die des Servers
                                (`korrektur_routes.handweg_lage`: `invoice_id is
                                None` ⇒ 409 `original_unbekannt`) — die Zeile sagt
                                damit nichts über ihre Herkunft. */}
                            {/* R16-W6: Abstand zwischen den zwei Aktionen — ohne ihn
                                lagen „Korrektur anlegen" und „Schliessen" aneinander
                                (Fehlklick auf die Nachbaraktion). */}
                            {/* Ausfuhr-Korrektur (Task 5): an einer Ausfuhrzeile
                                (`ausfuhr_art != null`) entfällt „Create correction" —
                                der Handweg antwortet dort sicher 409 `ausfuhrvorgang`. */}
                            {/* 🔴 Teil A (UX-Sitzung fce0123f, 04.11.2026): unter `sm`
                                steht die Zelle als SPALTE. In einer Zeile summieren
                                sich Satz und Knoepfe in der min-content-Breite der
                                Tabelle (gemessen: 513 px gegen ein 358-px-Papier),
                                das Papier scrollt und „Close" liegt rechts der Kante.
                                Gestapelt zaehlt nur noch das breiteste Kind. */}
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={SP.chip}
                              justifyContent="flex-end"
                              alignItems={{ xs: 'flex-end', sm: 'center' }}
                            >
                              {lage.satz && (
                                <Box
                                  sx={{ textAlign: 'right', maxWidth: '36ch' }}
                                >
                                  <Typography
                                    variant="caption"
                                    component="p"
                                    sx={{
                                      color: 'text.secondary',
                                      // Teil A.2: die 44-px-Trefferflaeche des Verweises
                                      // endet an ihrer AUSSENkante; ohne diesen Abstand
                                      // beginnt der Satz darin und ein Klick auf seine
                                      // letzte Zeile oeffnete die Rechnung. Derselbe
                                      // Tokenwert, kein Literal (Hausform `BestellLink`).
                                      // 🔴 Nur unter `sm` (R15-Wiedervorlage 2, W1): am
                                      // Schreibtisch stehen die Aktionen nebeneinander,
                                      // die Zeilenhoehe ist gegen den Stand vor A.2
                                      // gepinnt — dort kostet der Abstand 14 px ohne
                                      // Nutzen. Der Preis ist benannt: am Schreibtisch
                                      // liegt die letzte Satzzeile in der oberen
                                      // Trefferflaeche des Verweises (ein Ziel, kein
                                      // zweites daneben).
                                      mb: { xs: TEXT_LINK_TREFFERFLAECHE_SX.paddingBottom, sm: 0 },
                                    }}
                                  >
                                    {lage.satz}
                                  </Typography>
                                  {lage.zurRechnung && (
                                    <Link
                                      component={RouterLink}
                                      to={`/invoicing/${e.invoice_id}`}
                                      variant="caption"
                                      // Teil A.2: 44 px zum Treffen am Telefon (Regel 22
                                      // §8) — Muster `BestellLink`.
                                      // 🔴 Teil A.4: der negative UNTERrand entfaellt
                                      // am Telefon. Er laesst den 44-px-Kasten 14 px
                                      // unter den Textfluss ragen; „Close" steht nur
                                      // `spacing` (4 px) darunter, also lagen 9,9 px
                                      // der Verweisflaeche unter dem Knopf — ein Tipp
                                      // dort schloss den Refund. Ab `sm` bleibt der
                                      // Token unveraendert (Zeilenhoehe gepinnt).
                                      sx={TEXT_LINK_TREFFERFLAECHE_OHNE_UNTERRAND_SX}
                                      // Teil A.3 (S4b-Muster): der sichtbare Text ist in
                                      // jeder Zeile „Open invoice" — ohne eigenen Namen
                                      // tragen alle Verweise denselben. Kennung ist die
                                      // Rechnungsnummer, sonst die Rechnungs-ID. 🔴 Das
                                      // Feld der Liste heisst `nummer`; gesetzt wird es
                                      // von der Route (`refund_entscheidung_routes.py:132`,
                                      // `eintrag["nummer"] = nummern.get(...)`), nicht von
                                      // `_zeile_aus`. Ein `e.invoice_number` waere immer
                                      // leer und der Name fiele still auf die ID zurueck.
                                      aria-label={`Open invoice ${e.nummer || e.invoice_id}`}
                                    >
                                      Open invoice
                                    </Link>
                                  )}
                                </Box>
                              )}
                              {lage.knopf && (
                                <Button
                                  size="small"
                                  onClick={() => setAusfuhr(e)}
                                  // Teil A.1: am Telefon darf die Beschriftung
                                  // umbrechen. Sonst traegt sie allein ~184 px
                                  // min-content, und die Tabelle bleibt 104 px
                                  // breiter als das Papier — „Close" liegt weiter
                                  // rechts der Kante. Der zugaengliche Name bleibt
                                  // derselbe; ab `sm` steht sie wie bisher einzeilig.
                                  sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' } }}
                                >
                                  Issue export correction
                                </Button>
                              )}
                              {ZULAESSIGE_ARTEN.includes(e.art) && e.invoice_id != null
                                && e.ausfuhr_art == null && (
                                <Button size="small" onClick={() => setKorrektur(e)}>
                                  Create correction
                                </Button>
                              )}
                              {/* R16 W7 (WCAG 4.1.2): jeder Zeilenknopf heisst sichtbar
                                  „Close" — ohne `aria-label` tragen alle denselben
                                  zugaenglichen Namen. Vorbild `Betrieb.jsx:1242`. */}
                              <Button
                                size="small"
                                aria-label={`Refund ${e.refund_id} close`}
                                onClick={() => setZeile(e)}
                              >
                                Close
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              )}
              <Typography variant="caption" sx={{ display: 'block', mt: SP.chip, color: 'text.secondary' }}>
                {eintraege.length} open {eintraege.length === 1 ? 'decision' : 'decisions'}
              </Typography>
            </Box>
          )}
        </Stack>

      <SchliessMaske
        offen={zeile !== null}
        zeile={zeile || {}}
        onSchliessen={() => setZeile(null)}
        onErgebnis={schliessen}
      />

      <KorrekturDialog
        offen={korrektur !== null}
        refundId={korrektur?.refund_id}
        onSchliessen={() => setKorrektur(null)}
        onErgebnis={korrekturFertig}
      />

      <AusfuhrKorrekturDialog
        offen={ausfuhr !== null}
        refundId={ausfuhr?.refund_id}
        onSchliessen={() => setAusfuhr(null)}
        onErgebnis={ausfuhrFertig}
      />
    </>
  )
}
